import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Fade, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { sort } from 'fast-sort';
import {
  ConditionalTemplateAnswer_FieldsFragment,
  ConditionalTemplateAnswerFilterInput,
  useConditionalTemplateQuestion_GetConditionalTemplateQuestionsForEstateWorkItemQuery,
  useEstateWorkItem_UpdateEstateWorkItemDocumentGenerationTemplateMutation,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import RequestTemplateModal from './components/request-template-modal';
import TemplateProcessingModal from './components/template-processing-modal';
import TemplateQuestionAnswerSelect from './components/template-question-answer-select';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    color: '#1C2541',
    padding: '40px 0 0 0',
    overflow: 'auto',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    width: 640,
    alignSelf: 'center',
    textAlign: 'center',
  },
  title: {
    fontWeight: 700,
    fontSize: 32,
    marginBottom: 20,
  },
  contentContainer: {
    padding: theme.spacing(3),
    flex: 1,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 10,
  },
  content: {
    width: 640,
  },
  footerContainer: {
    height: 76,
    padding: '0 80px',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
  },
  backButton: {
    color: '#1C2541',
    cursor: 'pointer',
    borderBottom: '1px solid #1C2541',
    fontSize: 16,
    '&:hover': {
      color: '#1c96c5',
    },
  },
  actionButton: {
    borderRadius: '100px',
  },
  footerButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
  },
  chipPink: {
    color: '#000000',
    backgroundColor: '#E53DE8',
  },
  dotPink: {
    color: '#E53DE8',
  },
}));

type FormValues = {
  conditionalTemplateAnswerFilters: ConditionalTemplateAnswerFilterInput[];
};

const TemplateQuestionnairePage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { portalId, estateWorkItemId } = useParams<Record<string, string>>();

  const [isRequestTemplateModalOpen, setIsRequestTemplateModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [documentGenerationTemplateId, setDocumentGenerationTemplateId] = useState<string | null>(
    null,
  );

  const {
    data: conditionalTemplateQuestionsData,
    loading: conditionalTemplateQuestionsDataLoading,
  } = useConditionalTemplateQuestion_GetConditionalTemplateQuestionsForEstateWorkItemQuery({
    variables: { input: { portalId, estateWorkItemId } },
    onError: (e) => {
      showAlert({
        message: 'Error getting conditional template questions: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [
    updateEstateWorkItemDocumentGenerationTemplate,
    { loading: updateEstateWorkItemExecuting },
  ] = useEstateWorkItem_UpdateEstateWorkItemDocumentGenerationTemplateMutation({
    onCompleted: () => {
      history.push(`/home/${portalId}/task/${estateWorkItemId}/template-data-wizard`);
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating estate work item: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  useEffect(() => {
    if (isSubmitting) {
      const timer = setTimeout(() => {
        setIsSubmitting(false);
        if (documentGenerationTemplateId) {
          onContinueClick();
        } else {
          setIsRequestTemplateModalOpen(true);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, documentGenerationTemplateId]);

  const { handleSubmit, control, watch } = useForm<FormValues>();

  const {
    replace: replaceConditionalTemplateAnswerFilters,
    fields: conditionalTemplateAnswerFiltersFields,
  } = useFieldArray({
    control,
    name: 'conditionalTemplateAnswerFilters',
  });
  const conditionalTemplateAnswerFilters = watch('conditionalTemplateAnswerFilters');
  const controlledConditionalTemplateAnswerFiltersFields =
    conditionalTemplateAnswerFiltersFields.map((field, index) => {
      return {
        ...field,
        ...conditionalTemplateAnswerFilters[index],
      };
    });

  if (conditionalTemplateQuestionsDataLoading) {
    return (
      <HomeLayout>
        <Fade in>
          <div className={classes.container}>
            <Spinner />
          </div>
        </Fade>
      </HomeLayout>
    );
  }

  const conditionalTemplateQuestions = sort(conditionalTemplateQuestionsData?.data || []).asc(
    ({ sortOrder }) => sortOrder,
  );

  const handleAnswerChange = (
    index: number,
    conditionalTemplateAnswerFilter?: ConditionalTemplateAnswerFilterInput,
  ) => {
    const newConditionalTemplateAnswerFilters = [
      ...conditionalTemplateAnswerFilters.slice(0, index),
      conditionalTemplateAnswerFilter
        ? conditionalTemplateAnswerFilter
        : {
            conditionalTemplateQuestionId: conditionalTemplateQuestions[index].id,
            filterAnswerId: '',
          },
    ];
    replaceConditionalTemplateAnswerFilters(newConditionalTemplateAnswerFilters);
  };

  const submitForm = async () => {
    setIsSubmitting(true);
  };

  const onSelectAnswer = (index: number, selected?: ConditionalTemplateAnswer_FieldsFragment) => {
    setDocumentGenerationTemplateId(null);

    if (selected?.shouldRequestTemplate) {
      setIsSubmitting(true);
      return;
    }

    if (selected?.documentGenerationTemplateId) {
      setDocumentGenerationTemplateId(selected.documentGenerationTemplateId);
      setIsSubmitting(true);
      return;
    }

    if (index === conditionalTemplateQuestions.length - 1) {
      setIsSubmitting(true);
      return;
    }

    const question = conditionalTemplateQuestions[index];
    if (selected && question) {
      const filter = {
        conditionalTemplateQuestionId: question.id,
        filterAnswerId: selected.id,
      };
      handleAnswerChange(index, filter);
    } else {
      handleAnswerChange(index, undefined);
    }
  };

  const onContinueClick = () => {
    if (documentGenerationTemplateId) {
      updateEstateWorkItemDocumentGenerationTemplate({
        variables: {
          input: {
            id: estateWorkItemId,
            portalId,
            documentGenerationTemplateId,
          },
        },
      });
    }
  };

  const isProcessing = isSubmitting || updateEstateWorkItemExecuting;

  const onSubmit = () => {
    let unansweredIndex = conditionalTemplateAnswerFilters.findIndex(
      (filter) => !filter || !filter.filterAnswerId || !filter.conditionalTemplateQuestionId,
    );
    const hasUnAnsweredQuestions =
      conditionalTemplateAnswerFilters.length < conditionalTemplateQuestions.length;
    if (hasUnAnsweredQuestions) {
      unansweredIndex = conditionalTemplateAnswerFilters.length;
    }

    if (unansweredIndex < 0) {
      submitForm();
      return;
    }

    const unansweredQuestion = conditionalTemplateQuestions[unansweredIndex];
    if (unansweredQuestion) {
      showAlert({
        message: `Please answer question ${unansweredIndex + 1}: ${unansweredQuestion.question}`,
        severity: NotificationSeverity.WARNING,
      });
      return;
    }
  };

  return (
    <HomeLayout>
      <Fade in>
        <form className={classes.container} onSubmit={handleSubmit(submitForm)}>
          <div className={classes.headerContainer}>
            <Typography className={classes.title}>Details</Typography>
            <Typography>
              Trustate offers hundreds of pre-loaded documents. Provide the details below to
              generate the appropriate document for your review.
            </Typography>
          </div>
          <div className={classes.contentContainer}>
            <div className={classes.content}>
              {conditionalTemplateQuestions.map((question, index) => (
                <Controller
                  name={`conditionalTemplateAnswerFilters.${index}`}
                  control={control}
                  render={() => (
                    <TemplateQuestionAnswerSelect
                      key={question.id}
                      portalId={portalId}
                      question={question}
                      questionIndex={index}
                      totalQuestions={conditionalTemplateQuestions.length}
                      onChange={(selected) => onSelectAnswer(index, selected)}
                      value={
                        controlledConditionalTemplateAnswerFiltersFields[index]?.filterAnswerId
                      }
                      conditionalTemplateAnswerFilters={conditionalTemplateAnswerFilters.slice(
                        0,
                        index,
                      )}
                    />
                  )}
                />
              ))}
            </div>
          </div>
          <div className={classes.footerContainer}>
            <Typography
              className={classes.backButton}
              onClick={() => history.replace(`/home/${portalId}/assigned-tasks`)}
            >
              Back
            </Typography>
            <div className={classes.footerButtonsContainer}>
              <Button
                variant="contained"
                color="primary"
                className={classes.actionButton}
                type="button"
                onClick={onSubmit}
                disabled={isProcessing}
              >
                Continue
              </Button>
            </div>
          </div>
          {isRequestTemplateModalOpen && !isProcessing && (
            <RequestTemplateModal
              setShowModal={setIsRequestTemplateModalOpen}
              showModal={isRequestTemplateModalOpen}
            />
          )}
          {isProcessing && (
            <TemplateProcessingModal showModal={isProcessing} setShowModal={setIsSubmitting} />
          )}
        </form>
      </Fade>
    </HomeLayout>
  );
};

export default TemplateQuestionnairePage;
